import "../../styles/alumni-cards.css";
import "./index.css";

import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  Modal,
  Pagination,
  Select,
  Spin,
  Collapse,
  Row,
  Col,
  Typography,
  Space,
  Empty,
} from "antd";
import { SearchOutlined, FilterOutlined, UserOutlined, TeamOutlined } from "@ant-design/icons";

import MenuLayout from "../../components/Menu";
import formFields from "./formFields";
import Loading from "../../components/Loading";
import { getJobFieldName } from "../../utils/fieldMapper";
import { supabase } from "../../lib/supabase";
import ProfileCard from "../alum-map/ProfileCard";
import { 
  useAlumniCards, 
  formatSchoolInfo, 
  generateCardId,
  formatCareerInfo,
  formatEducationInfo,
  isCareerInfoEmpty,
  isEducationInfoEmpty
} from "../../utils/alumniCardUtils";

const { Meta } = Card;
const { Title } = Typography;

const PAGE_SIZE = 20;

const initialQueryParams = {
  ...formFields.reduce((acc, item) => {
    acc[item.key] = null;
    return acc;
  }, {}),
  page: 1,
};

const Search = () => {
  const navigate = useNavigate();
  const [alumniList, setAlumniList] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const queryParams = useRef(initialQueryParams);
  const [form] = Form.useForm();
  
  // Initialize form with default values
  useEffect(() => {
    form.setFieldsValue(initialQueryParams);
  }, [form]);
  const [pagination, setPagination] = useState({
    total: 0,
    current: 1,
  });
  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [expandedContact, setExpandedContact] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const { cardRefs, handleMouseMove, handleMouseLeave, setCardRef } = useAlumniCards();

  // Mouse tracking functions are now imported from shared utility

  const handlePaginationChange = (newPageValue) => {
    queryParams.current = {
      ...queryParams.current,
      page: newPageValue,
    };
    setPagination((prev) => {
      return {
        ...prev,
        current: newPageValue,
      };
    });
  };

  useEffect(() => {
    query();
  }, [queryParams.current]);

  const handleFinishForm = (formValues) => {
    const transformedFormValues = Object.entries(formValues).reduce(
      (acc, [key, value]) => {
        // Handle numeric fields that should be integers
        if (['q_schoolm_start_time', 'q_schoolm_class', 'q_schoolh_start_time', 'q_schoolh_class'].includes(key)) {
          if (value === undefined || value === null || value === '') {
            acc[key] = null;
          } else {
            // Clean and parse numeric values, removing any non-numeric characters except negative sign
            const cleanedValue = String(value).replace(/[^\d-]/g, '');
            const parsedValue = parseInt(cleanedValue, 10);
            acc[key] = isNaN(parsedValue) ? null : parsedValue;
          }
        } else {
          acc[key] = value === undefined ? null : value;
        }
        return acc;
      },
      {}
    ); // set all undefined value to null and clean numeric fields

    queryParams.current = {
      ...transformedFormValues,
      page: 1,
    };
    setPagination((prev) => {
      return {
        ...prev,
        current: 1,
      };
    });
  };

  const handleClearForm = () => {
    form.resetFields();
    form.submit();
  };

  async function query() {
    setLoading(true);
    const { data, error } = await supabase.rpc("query_filter", {
      ...queryParams.current,
      qsize: PAGE_SIZE,
    });

    setAlumniList(data?.data || []);
    setPagination((prev) => {
      return {
        ...prev,
        total: data?.total || 0,
      };
    });
    setLoading(false);
  }

  const checkUserStatus = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate("/login");
    }
  };

  const fetchUserProfile = async () => {
    setUserLoading(true);
    const { data, error } = await supabase.rpc("query_self", {});
    
    if (data?.data?.[0]) {
      setUserProfile(data.data[0]);
    }
    setUserLoading(false);
  };

  const handleSameMiddleSchoolClass = () => {
    if (!userProfile) return;
    
    // Create the form values for middle school class search
    const formValues = {
      ...initialQueryParams,
      q_schoolm_start_time: userProfile.schoolm_start_time,
      q_schoolm_class: userProfile.schoolm_class,
    };
    
    // Update form and trigger submission
    form.setFieldsValue(formValues);
    handleFinishForm(formValues);
  };

  const handleSameHighSchoolClass = () => {
    if (!userProfile) return;
    
    // Create the form values for high school class search
    const formValues = {
      ...initialQueryParams,
      q_schoolh_start_time: userProfile.schoolh_start_time,
      q_schoolh_class: userProfile.schoolh_class,
    };
    
    // Update form and trigger submission
    form.setFieldsValue(formValues);
    handleFinishForm(formValues);
  };

  const handleAllAlumni = () => {
    // Clear all filters and show all alumni
    const formValues = { ...initialQueryParams };
    
    // Update form and trigger submission
    form.setFieldsValue(formValues);
    handleFinishForm(formValues);
  };

  const handleClickCard = (item) => {
    setExpandedContact(item);
  };

  useEffect(() => {
    checkUserStatus();
    fetchUserProfile();
  }, []);

  // Separate name field from other filters
  const nameField = formFields.find(field => field.key === "q_name");
  const otherFields = formFields.filter(field => field.key !== "q_name");

  return (
    <MenuLayout>
      <div className="search-container">
        {/* User Shortcuts Section */}
        {userProfile && (
          <div className="user-shortcuts-section">
            <Card className="user-shortcuts-card">
              <div className="user-shortcuts-header">
                <Title level={4}>
                  <TeamOutlined /> 快速查找
                </Title>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<TeamOutlined />}
                    onClick={handleAllAlumni}
                    loading={loading}
                  >
                    全部校友
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<TeamOutlined />}
                    onClick={handleSameMiddleSchoolClass}
                    loading={loading}
                  >
                    同初中班级 ({userProfile.schoolm_start_time}级{userProfile.schoolm_class}班)
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<TeamOutlined />}
                    onClick={handleSameHighSchoolClass}
                    loading={loading}
                  >
                    同高中班级 ({userProfile.schoolh_start_time}级{userProfile.schoolh_class}班)
                  </Button>
                </Space>
              </div>
            </Card>
          </div>
        )}

        {/* Main Search Bar */}
        <div className="main-search-section">
          <Form
            form={form}
            onFinish={handleFinishForm}
            className="main-search-form"
          >
            <div className="search-bar-wrapper">
              <Form.Item name="q_name" className="main-search-item">
                <Input
                  size="large"
                  placeholder="搜索校友姓名..."
                  prefix={<SearchOutlined />}
                  className="main-search-input"
                />
              </Form.Item>
              <Button 
                type="primary" 
                size="large" 
                htmlType="submit"
                icon={<SearchOutlined />}
                className="main-search-button"
              >
                搜索
              </Button>
            </div>
          </Form>
        </div>

        {/* Advanced Filters */}
        <div className="advanced-filters-section">
          <Collapse 
            ghost 
            expandIconPosition="end"
            className="filters-collapse"
            activeKey={filtersExpanded ? ["1"] : []}
            onChange={(keys) => setFiltersExpanded(keys.includes("1"))}
            items={[
              {
                key: "1",
                label: (
                  <span className="filters-header">
                    <FilterOutlined />
                    高级筛选
                  </span>
                ),
                children: (
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinishForm}
                    className="advanced-filters-form"
                  >
                    <Row gutter={[16, 16]}>
                      {otherFields.map((item) => {
                        if (item.type === "input") {
                          return (
                            <Col xs={24} sm={12} md={8} lg={6} key={item.key}>
                              <Form.Item 
                                label={item.label} 
                                name={item.key}
                                className="filter-item"
                              >
                                <Input 
                                  placeholder={`请输入${item.label}`}
                                  className="filter-input"
                                />
                              </Form.Item>
                            </Col>
                          );
                        }

                        if (item.type === "select" && item.options) {
                          return (
                            <Col xs={24} sm={12} md={8} lg={6} key={item.key}>
                              <Form.Item
                                label={item.label}
                                name={item.key}
                                className="filter-item"
                              >
                                <Select
                                  placeholder={`请选择${item.label}`}
                                  options={item.options}
                                  allowClear
                                  className="filter-select"
                                />
                              </Form.Item>
                            </Col>
                          );
                        }

                        return null;
                      })}
                    </Row>
                    <div className="filter-actions">
                      <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                        应用筛选
                      </Button>
                      <Button onClick={handleClearForm}>
                        清空筛选
                      </Button>
                    </div>
                  </Form>
                )
              }
            ]}
          />
        </div>
      </div>

      <Modal
        // title={expandedContact?.name_cn}
        open={expandedContact}
        footer={null}
        width="70vw"
        className="detailed-profile-modal"
        onCancel={() => {
          setExpandedContact(null);
        }}
      >
        <ProfileCard profile={expandedContact}/>
        {/* <p>UI待施工：</p>
        <pre>{JSON.stringify(expandedContact, null, 2)}</pre> */}
      </Modal>
      <Spin tip="加载中..." indicator={Loading} spinning={loading}>
        <div className="alumni-list">
          {alumniList.length > 0 ? (
            alumniList.map((item, index) => {
              const cardId = generateCardId('card', index);
              
              return (
                <Card
                  key={item.id || `alumni-${index}`}
                  ref={(el) => setCardRef(cardId, el)}
                  bordered={false}
                  className="alumni-card"
                  onMouseMove={(e) => handleMouseMove(e, cardId)}
                  onMouseLeave={() => handleMouseLeave(cardId)}
                  onClick={() => {
                    handleClickCard(item);
                  }}
                >
                  <Meta
                    title={item.name_cn}
                    description={(() => {
                      const middleSchool = formatSchoolInfo(item.schoolm_start_time, item.schoolm_class, '初中');
                      const highSchool = formatSchoolInfo(item.schoolh_start_time, item.schoolh_class, '高中');
                      
                      const parts = [middleSchool, highSchool].filter(Boolean);
                      return parts.length > 0 ? parts.join(' | ') : '校友信息';
                    })()}
                  />
                  <Divider />
                  <div className="alumni-info">
                    <div className="info-section">
                      <div className="info-label">高等教育</div>
                      <p className={`info-content ${isEducationInfoEmpty(item) ? 'empty' : ''}`}>
                        {formatEducationInfo(item)}
                      </p>
                    </div>
                    <div className="info-section">
                      <div className="info-label">职业信息</div>
                      <p className={`info-content ${isCareerInfoEmpty(item) ? 'empty' : ''}`}>
                        {formatCareerInfo(item)}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <div className="no-results">
              <Empty
                description="暂无匹配的校友信息"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      </Spin>
      <Pagination
        current={pagination.current}
        total={pagination.total}
        pageSize={PAGE_SIZE}
        onChange={handlePaginationChange}
        className="pagination"
      />
    </MenuLayout>
  );
};

export default Search;
